import { useMutation } from "@tanstack/react-query";
import { BookOpenText, Eye, EyeOff, RefreshCw } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/ui/button.js";
import { Card } from "../../components/ui/card.js";
import { ApiError, api } from "../../lib/api.js";

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function highlightWords(passage: string, words: string[]): ReactNode[] {
  if (words.length === 0) return [passage];
  const pattern = words
    .map(escapeRegExp)
    .sort((a, b) => b.length - a.length)
    .join("|");
  const re = new RegExp(`\\b(?:${pattern})\\w*`, "gi");
  const parts: ReactNode[] = [];
  let last = 0;
  let key = 0;
  for (const match of passage.matchAll(re)) {
    const index = match.index ?? 0;
    if (index > last) parts.push(passage.slice(last, index));
    parts.push(
      <mark
        key={key++}
        className="rounded bg-[var(--primary)]/20 px-1 font-semibold text-[var(--accent)]"
      >
        {match[0]}
      </mark>,
    );
    last = index + match[0].length;
  }
  if (last < passage.length) parts.push(passage.slice(last));
  return parts;
}

export function ReadingPracticePage() {
  const { t } = useTranslation();
  const [showTranslation, setShowTranslation] = useState(false);

  const generate = useMutation({
    mutationFn: () => api.generateReading(),
    onSuccess: () => setShowTranslation(false),
  });
  const passage = generate.data;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight text-[var(--text-primary)]">
            {t("reading.title")}
          </h1>
          <p className="mt-1 text-[var(--text-secondary)]">{t("reading.subtitle")}</p>
        </div>
        <Button variant="primary" onClick={() => generate.mutate()} disabled={generate.isPending}>
          <RefreshCw className={`h-4 w-4 ${generate.isPending ? "animate-spin" : ""}`} aria-hidden />
          {generate.isPending
            ? t("reading.generating")
            : passage
              ? t("reading.generateAnother")
              : t("reading.generate")}
        </Button>
      </div>

      {generate.isError ? (
        <Card className="text-rose-300">
          {generate.error instanceof ApiError ? generate.error.message : t("reading.error")}
        </Card>
      ) : null}

      {!passage && !generate.isPending && !generate.isError ? (
        <Card className="flex flex-col items-center gap-3 py-12 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--primary)]/10 text-[var(--accent)]">
            <BookOpenText className="h-6 w-6" aria-hidden />
          </span>
          <p className="max-w-md text-sm text-[var(--text-secondary)]">{t("reading.emptyHint")}</p>
        </Card>
      ) : null}

      {generate.isPending ? <Card>{t("reading.generating")}</Card> : null}

      {passage ? (
        <>
          <Card className="space-y-4">
            <h2 className="font-display text-lg font-semibold text-[var(--text-primary)]">
              {passage.title}
            </h2>
            <p className="whitespace-pre-wrap text-base leading-relaxed text-[var(--text-secondary)]">
              {highlightWords(passage.passage, passage.words.map((w) => w.word))}
            </p>
            <div className="border-t border-[var(--glass-border)] pt-3">
              <button
                type="button"
                onClick={() => setShowTranslation((v) => !v)}
                className="inline-flex items-center gap-1.5 text-sm text-[var(--text-muted)] transition-colors hover:text-[var(--text-secondary)]"
              >
                {showTranslation ? (
                  <EyeOff className="h-4 w-4" aria-hidden />
                ) : (
                  <Eye className="h-4 w-4" aria-hidden />
                )}
                {showTranslation ? t("reading.hideTranslation") : t("reading.showTranslation")}
              </button>
              {showTranslation ? (
                <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-[var(--text-muted)]">
                  {passage.vietnamese_translation}
                </p>
              ) : null}
            </div>
          </Card>

          <Card className="space-y-3">
            <p className="text-sm font-medium text-[var(--text-primary)]">{t("reading.wordsLabel")}</p>
            <ul className="grid gap-2 md:grid-cols-2">
              {passage.words.map((word) => (
                <li key={word.word} className="rounded-lg border border-[var(--glass-border)] p-3">
                  <p className="text-sm font-semibold text-[var(--accent)]">{word.word}</p>
                  <p className="mt-0.5 text-xs text-[var(--text-secondary)]">{word.meaning}</p>
                  {word.vietnamese ? (
                    <p className="mt-0.5 text-xs text-[var(--text-muted)]">{word.vietnamese}</p>
                  ) : null}
                </li>
              ))}
            </ul>
          </Card>
        </>
      ) : null}
    </div>
  );
}
